/**
 * Ad placements for the Adsterra network.
 *
 * Every banner is its own srcdoc iframe: the network reads a global `atOptions`,
 * so two banners in one document would overwrite each other. The rails sit outside
 * #stage and are decided by window size only, so the stage never changes size
 * when a round starts.
 */

type AdsMode =
  | "title"
  | "mobile"
  | "badge"
  | "skins"
  | "mp"
  | "lobby"
  | "loading"
  | "warp"
  | "party"
  | "leave-q"
  | "pause"
  | "play"
  | "complete"
  | "vs-over"
  | "journey-over";

type BannerSlot = "title" | "loading" | "pause" | "complete" | "journey_over" | "vs_over";
type RailSlot = "rail_left" | "rail_right";
type Slot = BannerSlot | RailSlot;
type Format = "300x250" | "320x50" | "160x600";

interface Banner {
  w: number;
  h: number;
  key: string;
}

const BANNERS: Record<Format, Banner> = {
  "300x250": { w: 300, h: 250, key: "fb59f0702583394fade55e5a4e9fc04f" },
  "320x50": { w: 320, h: 50, key: "c5cf6d31972c2024f661b17b44ebe407" },
  "160x600": { w: 160, h: 600, key: "4c9799cf476548271f7b2080d3eded83" },
};

const POPUNDER_SRC = "https://abscloud.org/1/02736a973287f7cfbffd00df1b48d0d1";
const REUSE_MS = 30_000;
const POPUNDER_GAP_MS = 24 * 60 * 60 * 1000;

const BODY_SLOTS: BannerSlot[] = ["title", "loading", "pause", "complete", "journey_over", "vs_over"];
const MODE_SLOT: Partial<Record<AdsMode, BannerSlot>> = {
  title: "title",
  loading: "loading",
  pause: "pause",
  complete: "complete",
  "journey-over": "journey_over",
  "vs-over": "vs_over",
};

interface Mounted {
  box: HTMLElement;
  banner: Format;
  born: number;
}

const live = new Map<Slot, Mounted>();
/** Parked frames, kept for the 30 s reuse window and dropped after it. */
const spare = new Map<Slot, Mounted>();
const keepAlive = new Map<Slot, number>();
const rails = new Map<RailSlot, HTMLElement>();

let lastMode: AdsMode | null = null;
let popunder = false;

function track(event: string, params?: Record<string, unknown>): void {
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== "function") return;
  try {
    gtag("event", event, params ?? {});
  } catch {
    // Analytics must never break a round.
  }
}

export function trackGameStart(gameMode: string, siteKind: "demo" | "web"): void {
  track("game_start", { game_mode: gameMode, site_kind: siteKind });
}

export function trackJourneyNext(level: number): void {
  track("journey_next", { level });
}

export function trackSiteDestroyed(gameMode: string): void {
  track("site_destroyed", { game_mode: gameMode });
}

export function trackJourneyOver(level: number): void {
  track("journey_over", { level });
}

function bannerDoc(banner: Format): string {
  const { w, h, key } = BANNERS[banner];
  return [
    "<!doctype html><html><head><meta charset=\"utf-8\">",
    "<style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style>",
    "</head><body>",
    `<script>atOptions = {key:'${key}',format:'iframe',height:${h},width:${w},params:{}};</script>`,
    `<script src="https://bauval.org/22/${key}"></script>`,
    "</body></html>",
  ].join("");
}

function adFrame(banner: Format): HTMLIFrameElement {
  const { w, h } = BANNERS[banner];
  const frame = document.createElement("iframe");
  frame.className = "ad-frame";
  frame.title = "Advertisement";
  frame.width = String(w);
  frame.height = String(h);
  frame.scrolling = "no";
  frame.style.width = `${w}px`;
  frame.style.height = `${h}px`;
  frame.setAttribute("sandbox", "allow-scripts allow-popups allow-popups-to-escape-sandbox");
  frame.srcdoc = bannerDoc(banner);
  return frame;
}

function adShell(slot: Slot, banner: Format): HTMLElement {
  const box = document.createElement("div");
  box.className = "ad-slot";
  box.dataset.slot = slot;
  const label = document.createElement("p");
  label.className = "ad-slot-label";
  label.textContent = "Advertisement";
  box.append(label, adFrame(banner));
  return box;
}

/** Where a break-screen banner goes, and which sibling it sits in front of. */
function slotAnchor(slot: BannerSlot): { parent: HTMLElement; before: Element | null } | null {
  switch (slot) {
    case "title": {
      // Phones put the strip under the whole title page. Desktop uses the empty left
      // column of the title row, so the address row and mode picker keep the first screen.
      if (document.body.classList.contains("touch")) {
        const page = document.getElementById("title");
        return page ? { parent: page, before: null } : null;
      }
      return pick("#title > .title-row");
    }
    case "loading": {
      const page = document.getElementById("loading");
      return page ? { parent: page, before: null } : null;
    }
    case "pause": {
      // Right under Resume / Restart / New: the card is taller than most screens,
      // so the end of it is never seen.
      const body = document.querySelector<HTMLElement>("#pause .menu-card .sfwin-body");
      const row = body?.querySelector(":scope > .row");
      return body ? { parent: body, before: row?.nextElementSibling ?? null } : null;
    }
    case "complete":
      return pick("#complete .sfwin-body");
    case "journey_over":
      return pick("#journey-over .sfwin-body");
    case "vs_over":
      return pick("#vs-over .sfwin-body");
  }
}

function pick(selector: string): { parent: HTMLElement; before: Element | null } | null {
  const parent = document.querySelector<HTMLElement>(selector);
  return parent ? { parent, before: null } : null;
}

function bannerFor(slot: Slot): Format {
  if (slot === "rail_left" || slot === "rail_right") return "160x600";
  if (!document.body.classList.contains("touch")) return "300x250";
  if (slot === "title" || slot === "pause") return "320x50";
  return window.innerHeight >= 560 ? "300x250" : "320x50";
}

function mount(slot: Slot, host: HTMLElement, before: Element | null = null): void {
  const banner = bannerFor(slot);
  const now = performance.now();
  const again = spare.get(slot);
  spare.delete(slot);
  window.clearTimeout(keepAlive.get(slot));
  keepAlive.delete(slot);
  if (again && again.banner === banner && now - again.born < REUSE_MS) {
    live.set(slot, again);
    again.box.classList.remove("ad-parked");
    // Re-inserting a moved iframe reloads it, so leave the node alone when it is already in place.
    if (again.box.parentElement !== host || again.box.nextSibling !== before) host.insertBefore(again.box, before);
    return;
  }
  again?.box.remove();
  const box = adShell(slot, banner);
  live.set(slot, { box, banner, born: now });
  host.insertBefore(box, before);
  track("ad_slot_shown", { ad_slot: slot, ad_format: banner });
}

/**
 * Hide a frame and drop it for real once the reuse window is over. Detaching the
 * node would destroy the nested document, and putting it back re-parses the srcdoc
 * and asks the ad server again, which is the hammering this window exists to stop.
 */
function unmount(slot: Slot): void {
  const mounted = live.get(slot);
  if (!mounted) return;
  live.delete(slot);
  const stale = spare.get(slot);
  stale?.box.remove();
  spare.set(slot, mounted);
  mounted.box.classList.add("ad-parked");
  window.clearTimeout(keepAlive.get(slot));
  const left = Math.max(0, REUSE_MS - (performance.now() - mounted.born));
  keepAlive.set(slot, window.setTimeout(() => {
    keepAlive.delete(slot);
    if (spare.get(slot) !== mounted) return;
    spare.delete(slot);
    mounted.box.remove();
  }, left));
}

/** Mount, remount after a format change, or leave an up-to-date frame alone. */
function open(slot: BannerSlot): void {
  const mounted = live.get(slot);
  const banner = bannerFor(slot);
  if (mounted && mounted.banner === banner) return;
  const anchor = slotAnchor(slot);
  if (mounted) unmount(slot);
  if (anchor) mount(slot, anchor.parent, anchor.before);
}

function railHost(slot: RailSlot): HTMLElement | null {
  const have = rails.get(slot);
  if (have) return have;
  const body = document.querySelector<HTMLElement>(".window-body");
  const stageEl = document.getElementById("stage");
  if (!body || !stageEl) return null;
  const side = slot === "rail_left" ? "left" : "right";
  const el = document.createElement("aside");
  el.className = `ad-rail ad-rail-${side}`;
  el.setAttribute("aria-label", "Advertisement");
  if (side === "left") body.insertBefore(el, stageEl);
  else body.append(el);
  rails.set(slot, el);
  return el;
}

function hideRail(slot: RailSlot): void {
  unmount(slot);
  const el = rails.get(slot);
  if (el) el.hidden = true;
}

/**
 * Rails only follow the window, never the mode, so the stage keeps its size when
 * a round starts. 1400px is the first width with room for the board plus a 160px
 * rail and a 24px gap.
 */
function syncRails(): void {
  const touch = document.body.classList.contains("touch");
  const on = !touch && window.innerHeight >= 700;
  const right = on && window.innerWidth >= 1400;
  const left = on && window.innerWidth >= 1800;
  if (right) {
    const host = railHost("rail_right");
    if (host) {
      host.hidden = false;
      if (!live.has("rail_right")) mount("rail_right", host);
    }
  } else hideRail("rail_right");
  if (left) {
    const host = railHost("rail_left");
    if (host) {
      host.hidden = false;
      if (!live.has("rail_left")) mount("rail_left", host);
    }
  } else hideRail("rail_left");
}

function readStore(key: string): { ok: boolean; value: number | null } {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return { ok: true, value: null };
    const value = Number(raw);
    return { ok: true, value: Number.isFinite(value) ? value : null };
  } catch {
    return { ok: false, value: null };
  }
}

function writeStore(key: string, value: number): boolean {
  try {
    localStorage.setItem(key, String(value));
    return true;
  } catch {
    return false;
  }
}

/** Count one finished round. A storage failure leaves the popunder unarmed. */
function countBreak(): number | null {
  const held = readStore("daw_breaks");
  if (!held.ok) return null;
  const next = (held.value ?? 0) + 1;
  return writeStore("daw_breaks", next) ? next : null;
}

/**
 * Popunder: top-level page only, not on a player's first finished round, and at
 * most once a day. One script per page.
 */
function armPopunder(): void {
  const breaks = countBreak();
  if (popunder) return;
  if (window.top !== window) return;
  if (breaks === null || breaks < 2) return;
  const last = readStore("daw_pop_at");
  if (!last.ok) return;
  if (last.value !== null && Date.now() - last.value < POPUNDER_GAP_MS) return;
  if (!writeStore("daw_pop_at", Date.now())) return;
  const script = document.createElement("script");
  script.setAttribute("data-cfasync", "false");
  script.src = POPUNDER_SRC;
  document.head.append(script);
  popunder = true;
  track("popunder_armed");
}

export function syncAds(mode: AdsMode): void {
  const entered = lastMode !== mode;
  lastMode = mode;
  syncRails();
  const want = MODE_SLOT[mode];
  for (const slot of BODY_SLOTS) {
    if (slot === want) open(slot);
    else if (live.has(slot)) unmount(slot);
  }
  if (entered && (mode === "complete" || mode === "journey-over" || mode === "vs-over")) armPopunder();
}

if (typeof window !== "undefined") {
  window.addEventListener("resize", () => {
    syncRails();
    const want = lastMode ? MODE_SLOT[lastMode] : undefined;
    if (want) open(want);
  });
}
